import React, { MouseEvent, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";

import PaperPopper from "veneer/PaperPopper";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import {
  Indicator,
  IndicatorColor,
  IndicatorType,
} from "cortex/ui/src/ui/indicator";

interface RowProps {
  color: IndicatorColor;
  icon: IndicatorType;
  label: string;
  time: string;
}

const PopperWrapper = styled(PaperPopper)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: theme.shape.borderRadius * 2,
  },
}));

function Row({ color, icon, label, time }: RowProps) {
  return (
    <Stack
      alignItems="center"
      direction="row"
      gap={1}
      justifyContent="space-between"
    >
      <Stack alignItems="center" direction="row" gap={1} width="max-content">
        <Indicator color={color} type={icon} />
        <Typography
          color={Colors.gray1000}
          component="span"
          fontWeight="bold"
          variant="h6"
        >
          {label}
        </Typography>
      </Stack>
      <Typography color={Colors.gray1000}>{time}</Typography>
    </Stack>
  );
}

export default function LastConnectedHeader() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Stack alignItems="center" direction="row" gap={0.5}>
        <Typography fontWeight="bold">Connectivity</Typography>
        <span
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ lineHeight: 1 }}
        >
          <InfoIcon sx={{ fontSize: 16 }} />
        </span>
      </Stack>

      <PopperWrapper
        anchorEl={anchorEl}
        dataTestId="last-connected-legend"
        disablePortal={false}
        elevation={4}
        open={Boolean(anchorEl)}
        placement="right-start"
      >
        <Stack gap={2} pb={1} pt={1.5} px={1} width={260}>
          <Typography
            color={Colors.gray1000}
            component="span"
            fontWeight="bold"
            variant="h6"
          >
            Connectivity Tiers
          </Typography>

          <Stack gap={0.75} px={1}>
            <Row
              color="green"
              icon="triangle-up"
              label="Connected"
              time="<10m"
            />
            <Row
              color="yellow"
              icon="triangle-down"
              label="Missed Check-in"
              time="10-60m"
            />
            <Row
              color="darkYellow"
              icon="triangle-down"
              label="Offline"
              time="1-24h"
            />
            <Row
              color="red"
              icon="triangle-down"
              label="Unreachable"
              time=">1d"
            />
            <Row
              color="gray"
              icon="circle"
              label="Never Connected"
              time="Never"
            />
          </Stack>

          <Typography variant="caption">
            Our system pings your device to confirm connection status every 10
            minutes.
          </Typography>
        </Stack>
      </PopperWrapper>
    </>
  );
}
